import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CaseDashboardModel } from '@/components/case-dashboard/dashboard-view'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/utils/date'

/** When the file was opened and when it last learned something; the case overview shows them too. */
export function DashboardFacts({ dashboard }: { dashboard: CaseDashboardModel }) {
  const { t, i18n } = useTranslation()
  const facts = [
    {
      term: t('caseDashboard.openSince'),
      value: formatDate(dashboard.openedAt, i18n.language),
    },
    {
      term: t('caseDashboard.lastIntake'),
      value: dashboard.lastIntakeAt
        ? formatDate(dashboard.lastIntakeAt, i18n.language)
        : t('caseDashboard.noIntake'),
    },
  ]
  return (
    <dl className="flex flex-wrap gap-x-8 gap-y-2">
      {facts.map(fact => (
        <div key={fact.term} className="space-y-0.5">
          <dt className="text-xs text-muted-foreground">{fact.term}</dt>
          <dd className="text-sm font-medium text-foreground">{fact.value}</dd>
        </div>
      ))}
    </dl>
  )
}

export function DashboardLoading() {
  const { t } = useTranslation()
  return (
    <div
      className="flex flex-col gap-6"
      role="status"
      aria-busy
      aria-label={t('caseDashboard.loading')}
    >
      <Skeleton className="h-14 w-72" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {['sources', 'newObjects', 'alerts'].map(key => (
          <Skeleton key={key} className="h-28 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-80 rounded-xl" />
    </div>
  )
}

export function DashboardFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  return (
    <Card role="alert" className="flex flex-col items-start gap-3 rounded-xl p-5">
      <p className="text-sm text-foreground">{t('caseDashboard.failed')}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="size-3.5" />
        {t('caseDashboard.retry')}
      </Button>
    </Card>
  )
}
